import React, { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import AdminWarningList from '../../components/admin/AdminWarningList';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import { defaultUtcRangeDays } from '../../admin/adminHubUtils';
import { copyTextToClipboard, exportAdminTableAsCsv, exportAdminTableAsJson, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { filterAdminTableRows, nextSortState, sortAdminTableRows } from '../../admin/adminTableClient';

const AI_PREVIEW_TABLE_KEYS = ['metric_key', 'value'];

export default function AdminCommercialAiPage() {
  const { t } = useLanguage();
  const def = defaultUtcRangeDays(14);
  const [planCode, setPlanCode] = useState('free');
  const [userKey, setUserKey] = useState('');
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [fMetric, setFMetric] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [compact, setCompact] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const q = new URLSearchParams();
      q.set('plan_code', planCode.trim() || 'free');
      if (userKey.trim()) {
        q.set('user_key', userKey.trim());
        q.set('from', from.trim());
        q.set('to', to.trim());
      }
      const res = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN_COMMERCIAL_AI_ALLOWANCE_PREVIEW)}?${q}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch {
      setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const preview = data?.preview;
  const allowance = preview?.allowanceOnly || preview?.allowance;
  const rollup = preview && preview.usedUnitsInWindow != null ? preview : null;

  const previewRows = useMemo(() => {
    if (!data) return [];
    const rows = [
      { __rowKey: 'plan', metric_key: 'plan_code', value: String(data.planCode || '') },
      {
        __rowKey: 'limit',
        metric_key: 'ai_limit_units',
        value: allowance?.limit != null ? String(allowance.limit) : 'unlimited',
      },
      { __rowKey: 'used', metric_key: 'ai_used_units', value: allowance?.used != null ? String(allowance.used) : '' },
      {
        __rowKey: 'rem',
        metric_key: 'ai_remaining_units',
        value: allowance?.remaining != null ? String(allowance.remaining) : 'unlimited',
      },
      { __rowKey: 'src', metric_key: 'allowance_source', value: String(allowance?.source || '') },
      {
        __rowKey: 'enf',
        metric_key: 'enforcement',
        value: String(allowance?.enforcement || preview?.enforcement || ''),
      },
    ];
    if (allowance?.warning) {
      rows.push({ __rowKey: 'warn', metric_key: 'warning_code', value: String(allowance.warning) });
    }
    if (rollup && userKey.trim()) {
      rows.push({
        __rowKey: 'win',
        metric_key: 'used_units_in_window',
        value: String(rollup.usedUnitsInWindow ?? ''),
      });
      rows.push({ __rowKey: 'wf', metric_key: 'rollup_from', value: String(from.trim()) });
      rows.push({ __rowKey: 'wt', metric_key: 'rollup_to', value: String(to.trim()) });
    }
    return rows;
  }, [data, allowance, preview, rollup, userKey, from, to]);

  const tableCols = useMemo(
    () => [
      { key: 'metric_key', header: t('adminSnapshot.colMetricKey') },
      { key: 'value', header: t('adminKit.colValue') },
    ],
    [t]
  );

  const filteredRows = useMemo(
    () =>
      sortAdminTableRows(
        filterAdminTableRows(previewRows, AI_PREVIEW_TABLE_KEYS, search, { metric_key: fMetric }),
        sortKey,
        sortDir
      ),
    [previewRows, search, fMetric, sortKey, sortDir]
  );

  const onSort = useCallback(
    (key) => {
      const n = nextSortState(sortKey, sortDir, key);
      setSortKey(n.key);
      setSortDir(n.dir);
    },
    [sortKey, sortDir]
  );

  const warnItems = useMemo(() => {
    if (!allowance?.warning) return [];
    return [{ code: allowance.warning, message: t('adminKit.aiWarningHint'), tone: 'soft' }];
  }, [allowance, t]);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navAiUsagePolicies') },
    ],
    [t]
  );

  const hasFilters = !!(search.trim() || fMetric.trim());

  return (
    <AdminHubPageFrame
      titleKey="adminHub.aiUsagePoliciesTitle"
      descKey="adminHub.aiUsagePoliciesDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminFilterBar>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.planCode')}
            <input className="admin-hub-kit-toolbar__input" value={planCode} onChange={(e) => setPlanCode(e.target.value)} />
          </label>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.userKeyOptional')}
            <input className="admin-hub-kit-toolbar__input" value={userKey} onChange={(e) => setUserKey(e.target.value)} placeholder="ydim_…" />
          </label>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.dateFrom')}
            <input className="admin-hub-kit-toolbar__input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.dateTo')}
            <input className="admin-hub-kit-toolbar__input" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        <p className="admin-section-desc">{t('adminHub.aiAllowanceNote')}</p>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {data ? (
          <div className="admin-hub-kit-stack">
            <AdminWarningList items={warnItems} />
            <AdminSectionHeader title={t('adminKit.aiAllowanceTitle')} subtitle={t('adminKit.aiAllowanceSub')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.planCode')} value={data.planCode || '—'} />
              {rollup ? (
                <AdminMetricCard
                  label={t('adminKit.aiUsedUnitsWindow')}
                  value={rollup.usedUnitsInWindow ?? '—'}
                  hint={t('adminKit.aiRollupWindowHint')}
                />
              ) : null}
              <AdminMetricCard label={t('adminKit.aiLimit')} value={allowance?.limit != null ? String(allowance.limit) : t('adminKit.unlimited')} />
              <AdminMetricCard label={t('adminKit.aiUsed')} value={allowance?.used != null ? String(allowance.used) : '—'} />
              <AdminMetricCard
                label={t('adminKit.aiRemaining')}
                value={allowance?.remaining != null ? String(allowance.remaining) : t('adminKit.unlimited')}
              />
              <AdminMetricCard label={t('adminKit.allowanceSource')} value={allowance?.source || '—'} />
              <AdminMetricCard label={t('adminKit.enforcementMode')} value={allowance?.enforcement || preview?.enforcement || '—'} />
            </div>
            <p className="admin-hub-kit-note">{t('adminKit.aiTopTypesNote')}</p>

            <AdminSectionHeader title={t('adminExport.aiPreviewTableTitle')} level={3} />
            <AdminTableToolbar
              search={search}
              onSearchChange={setSearch}
              filteredCount={filteredRows.length}
              totalCount={previewRows.length}
              compact={compact}
              onCompactChange={setCompact}
              onExportCsv={() => exportAdminTableAsCsv(filteredRows, AI_PREVIEW_TABLE_KEYS, 'ai-allowance-preview')}
              onExportJson={() => exportAdminTableAsJson(filteredRows, AI_PREVIEW_TABLE_KEYS, 'ai-allowance-preview')}
              onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(filteredRows, AI_PREVIEW_TABLE_KEYS)))}
              filters={[{ id: 'mk', label: t('adminSnapshot.colMetricKey'), value: fMetric, onChange: setFMetric }]}
            />
            <AdminDataTable
              columns={tableCols}
              rows={filteredRows}
              emptyMessage={hasFilters ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
              emptyVariant={hasFilters ? 'noResults' : 'default'}
              compact={compact}
              sortable
              sortKey={sortKey}
              sortDir={sortDir}
              onSortChange={onSort}
              copyRowKeys={AI_PREVIEW_TABLE_KEYS}
            />

            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
